import { getUserId } from "services/cookieService";
import {
  fetchCart,
  removeInvalidItems,
  selectCartItems,
} from "store/slices/cartSlice";
import {
  closeGlobalDialog,
  openGlobalDialog,
} from "store/slices/globalDialogSlice";
import { AppDispatch, RootState } from "store/Store";
import { ICartItem } from "store/types/cartTypes";
import { isKit } from "./itemTypeHelpers";

interface ProductChangePayload {
  product: {
    id: string;
    variants: string[];
  };
}


const findInvalidItems = (
  cartItems: ICartItem[],
  validProducts: ProductChangePayload[]
): ICartItem[] => {
  const validProductIds = new Set(
    validProducts.map(({ product }) => product.id)
  );
  const validMap = new Map<string, Set<string>>();
  validProducts.forEach(({ product }) =>
    validMap.set(product.id, new Set(product.variants))
  );

  return cartItems.filter((item) => {
    const validVariants = validMap.get(item.productId);

    // Invalid if productId is not in the response
    if (!validProductIds.has(item.productId)) return true;

    // For kits, if the backend returns no variants, we consider it valid if found
    if (isKit(item) && validVariants && validVariants.size === 0) {
      return false;
    }

    // Otherwise, check if variantId is valid
    const isInvalid = validVariants && !validVariants.has(item.variantId);
    return isInvalid;
  });
};

const showInvalidItemsDialog = (
  dispatch: AppDispatch,
  message: string,
  onConfirm: () => void
) => {
  dispatch(
    openGlobalDialog({
      message,
      onConfirm,
    })
  );
};

export const handleOnProductChange = (
  dispatch: AppDispatch,
  getState: () => RootState,
  payload: any
) => {
  const userId = getUserId();
  const unavailableMsg = "Some products in your cart are no longer available.";

  // Normalize payload: handle both array format and new object format
  let normalizedPayload: ProductChangePayload[] = [];
  if (Array.isArray(payload)) {
    normalizedPayload = payload;
  } else if (payload && typeof payload === "object") {
    const products = payload.products || [];
    const productGroups = payload.productGroups || [];
    normalizedPayload = [
      ...products,
      ...productGroups.map((pg: any) => ({
        product: {
          id: pg.productGroup.productGroupId,
          variants: pg.productGroup.productGroup ? [] : ["__UNAVAILABLE__"],
        },
      })),
    ];
  }

  if (normalizedPayload.length === 0) return;

  const payloadProductIds = new Set(
    normalizedPayload.map(({ product }) => product.id)
  );

  if (userId) {
    const cartItems = selectCartItems(getState()) as ICartItem[];
    const affectedCartItems = cartItems.filter((item) =>
      payloadProductIds.has(item.productId)
    );
    if (affectedCartItems.length > 0) {
      const invalidItems = findInvalidItems(affectedCartItems, normalizedPayload);
      if (invalidItems.length > 0) {
        showInvalidItemsDialog(dispatch, unavailableMsg, () => {
          dispatch(fetchCart({ userId, isAlertRequired: false }));
          dispatch(removeInvalidItems(invalidItems));
          dispatch(closeGlobalDialog());
        });
      }
    }
  } else {
    const localCart: ICartItem[] = JSON.parse(
      localStorage.getItem("cart") || "[]"
    );
    const affectedCartItems = localCart.filter((item) =>
      payloadProductIds.has(item.productId)
    );
    if (affectedCartItems.length > 0) {
      const invalidItems = findInvalidItems(affectedCartItems, normalizedPayload);
      if (invalidItems.length > 0) {
        showInvalidItemsDialog(dispatch, unavailableMsg, () => {
          dispatch(removeInvalidItems(invalidItems));
          dispatch(closeGlobalDialog());
        });
      }
    }
  }
};
